/**
 * Phase 10.1 — perturbation preset rules for the New Campaign dialog.
 *
 * Three presets:
 *   - Nominal   : no overrides → backend resolves every variable to
 *                 its platform-default distribution.
 *   - Stressing : pessimistic shift on ~12 high-impact variables that
 *                 stress the engagement (degraded sensor, harsher
 *                 environment, more agile threat, slightly under-
 *                 performing interceptor). Wider spreads than nominal.
 *   - Worst-case: stressing + worst-corner means on additional
 *                 variables. Designed to drive Pk well below 1 even
 *                 on otherwise winnable scenarios so the operator
 *                 sees the failure-mode distribution.
 *
 * All presets emit JSON-shaped Distribution specs the backend
 * understands directly:
 *   { kind: "uniform"|"normal"|"triangular", params: {...} }
 *
 * Presets are computed against the ``resolved_specs`` map returned by
 * ``GET /api/pk-campaigns/registry?scenario_id=…`` so the lo/hi/mean
 * values respect the current scenario's nominal field values.
 */

export const PRESET_NAMES = ["nominal", "stressing", "worst_case"];

/** Read a numeric nominal off the resolved-specs map; default 0. */
function nominalOf(resolved, key) {
    const spec = resolved?.[key];
    if (!spec) return null;
    const n = Number(spec.nominal);
    return Number.isFinite(n) ? n : 0;
}

function clamp01(v) {
    if (v < 0) return 0;
    if (v > 1) return 1;
    return v;
}

/**
 * Nominal preset — empty overrides. Backend uses platform defaults.
 */
export function presetNominal() {
    return {};
}

/**
 * Stressing preset — curated pessimistic shifts.
 * Targets ~12 variables across Sensor / Environmental / Threat /
 * Payload / Interceptor that have the largest downstream effect on
 * Pk. Other variables remain at platform defaults.
 */
export function presetStressing(resolvedSpecs) {
    const r = resolvedSpecs || {};
    const o = {};

    // Sensor degradation
    const detRange = nominalOf(r, "sensor.detection_range_m");
    if (detRange != null) {
        o["sensor.detection_range_m"] = {
            kind: "normal",
            params: { mean: detRange * 0.85, std: Math.max(detRange * 0.10, 50) },
        };
    }
    o["sensor.noise_std_m"] = {
        kind: "uniform",
        params: { lo: 0.5, hi: 2.5 },
    };
    o["sensor.track_drop_probability"] = {
        kind: "uniform",
        params: { lo: 0.0, hi: 0.10 },
    };
    const detDelay = nominalOf(r, "sensor.detection_delay_s");
    if (detDelay != null) {
        o["sensor.detection_delay_s"] = {
            kind: "uniform",
            params: { lo: Math.max(detDelay, 0.5), hi: Math.max(detDelay * 2, 1.0) },
        };
    }

    // Environment harshness
    o["environment.gust_std_mps"] = {
        kind: "uniform",
        params: { lo: 1.0, hi: 3.0 },
    };
    o["environment.wind_mps.x"] = {
        kind: "normal",
        params: { mean: 2.0, std: 2.0 },
    };
    o["environment.wind_mps.y"] = {
        kind: "normal",
        params: { mean: 1.0, std: 2.0 },
    };

    // Threat aggressiveness (faster, more agile)
    const tgtSpd = nominalOf(r, "target.max_speed_mps");
    if (tgtSpd != null) {
        o["target.max_speed_mps"] = {
            kind: "uniform",
            params: { lo: tgtSpd * 0.95, hi: tgtSpd * 1.20 },
        };
    }
    const tgtAcc = nominalOf(r, "target.acceleration_mps2");
    if (tgtAcc != null) {
        o["target.acceleration_mps2"] = {
            kind: "uniform",
            params: { lo: tgtAcc * 0.95, hi: tgtAcc * 1.25 },
        };
    }
    const tgtTurn = nominalOf(r, "target.max_turn_rate_dps");
    if (tgtTurn != null) {
        o["target.max_turn_rate_dps"] = {
            kind: "uniform",
            params: { lo: tgtTurn * 0.90, hi: tgtTurn * 1.30 },
        };
    }

    // Payload reliability dip
    const pe = nominalOf(r, "payload.probability_of_effect");
    if (pe != null) {
        const mode = clamp01(pe * 0.85);
        o["payload.probability_of_effect"] = {
            kind: "triangular",
            params: {
                lo: clamp01(mode - 0.10),
                mode,
                hi: clamp01(mode + 0.10),
            },
        };
    }

    // Interceptor slightly under
    const intSpd = nominalOf(r, "interceptor.max_speed_mps");
    if (intSpd != null) {
        o["interceptor.max_speed_mps"] = {
            kind: "normal",
            params: { mean: intSpd * 0.95, std: intSpd * 0.05 },
        };
    }

    return o;
}

/**
 * Worst-case preset — Stressing + harder shifts on additional vars.
 * Extends Stressing with more aggressive sensor / interceptor
 * degradation and tightens payload reliability further. Designed to
 * drive the failure-mode breakdown into seeker_lock_failed,
 * guidance_saturated, target_escaped territory.
 */
export function presetWorstCase(resolvedSpecs) {
    const o = presetStressing(resolvedSpecs);
    const r = resolvedSpecs || {};

    // Harsher sensor
    const detRange = nominalOf(r, "sensor.detection_range_m");
    if (detRange != null) {
        o["sensor.detection_range_m"] = {
            kind: "normal",
            params: { mean: detRange * 0.70, std: Math.max(detRange * 0.15, 75) },
        };
    }
    o["sensor.noise_std_m"] = {
        kind: "uniform",
        params: { lo: 1.0, hi: 4.0 },
    };
    o["sensor.track_drop_probability"] = {
        kind: "uniform",
        params: { lo: 0.02, hi: 0.20 },
    };
    o["sensor.reacquisition_delay_s"] = {
        kind: "uniform",
        params: { lo: 1.0, hi: 3.0 },
    };
    const upd = nominalOf(r, "sensor.update_rate_hz");
    if (upd != null) {
        o["sensor.update_rate_hz"] = {
            kind: "uniform",
            params: { lo: Math.max(upd * 0.5, 1), hi: upd * 0.9 },
        };
    }

    // Harsher environment
    o["environment.gust_std_mps"] = {
        kind: "uniform",
        params: { lo: 2.0, hi: 5.0 },
    };
    o["environment.wind_mps.x"] = {
        kind: "normal",
        params: { mean: 4.0, std: 3.0 },
    };

    // Tighter payload reliability
    const pe = nominalOf(r, "payload.probability_of_effect");
    if (pe != null) {
        const mode = clamp01(pe * 0.70);
        o["payload.probability_of_effect"] = {
            kind: "triangular",
            params: {
                lo: clamp01(mode - 0.15),
                mode,
                hi: clamp01(mode + 0.15),
            },
        };
    }

    // Heavier interceptor underperformance
    const intSpd = nominalOf(r, "interceptor.max_speed_mps");
    if (intSpd != null) {
        o["interceptor.max_speed_mps"] = {
            kind: "normal",
            params: { mean: intSpd * 0.90, std: intSpd * 0.07 },
        };
    }
    const intTurn = nominalOf(r, "interceptor.max_turn_rate_dps");
    if (intTurn != null) {
        o["interceptor.max_turn_rate_dps"] = {
            kind: "uniform",
            params: { lo: intTurn * 0.80, hi: intTurn * 1.05 },
        };
    }

    return o;
}

/** Apply a preset by name; unknown name → empty overrides. */
export function applyPreset(name, resolvedSpecs) {
    if (name === "stressing") return presetStressing(resolvedSpecs);
    if (name === "worst_case") return presetWorstCase(resolvedSpecs);
    return presetNominal();
}

/** Format a Distribution spec for display ("uniform[70.0, 90.0]"). */
export function formatDistribution(spec) {
    if (!spec || !spec.kind || !spec.params) return "—";
    const p = spec.params;
    const round = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return "—";
        return Math.abs(n) < 0.01 ? n.toFixed(4) : n.toFixed(3);
    };
    if (spec.kind === "uniform") {
        return `uniform[${round(p.lo)}, ${round(p.hi)}]`;
    }
    if (spec.kind === "normal") {
        return `normal(μ=${round(p.mean)}, σ=${round(p.std)})`;
    }
    if (spec.kind === "triangular") {
        return `tri[${round(p.lo)}, mode=${round(p.mode)}, ${round(p.hi)}]`;
    }
    return spec.kind;
}
